/* In-browser PDF Form viewer for the Deal Jacket.
 *
 * Stack: pdfjs-dist (render) + pdf-lib (fill / flatten / save).
 *
 * Flow:
 *   1. Mount → fetch /fill-data → returns template metadata + resolved
 *      acro_values + overlay_values + signed pdf_url
 *   2. Download PDF bytes (auth + signed link → both belt-and-suspenders)
 *   3. pdf-lib autofills AcroForm fields into a working copy
 *   4. pdfjs renders each page to canvas
 *   5. React overlays editable inputs:
 *        - For AcroForm widgets: positioned by `rect` from acro_fields
 *        - For flat-scan overlay fields: positioned by x/y/w/h percentages
 *   6. User edits in place; "Print" composes a flattened PDF on the fly
 *      and either window.opens it (print) or POSTs it back to /save-filled-pdf
 *   7. Every state change (view / autofill / edit / print / save / download)
 *      is audit-logged via /pdf-log.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

// Worker is served from the same CDN as the matching pdfjs-dist version.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

const TOKEN_KEY = "ssc_token";

/* Fetch an auth-protected URL into an ArrayBuffer. The Bearer header is
 * required for non-signed paths; signed URLs work without it but adding
 * one is harmless. */
async function authArrayBuffer(url) {
  const token = localStorage.getItem(TOKEN_KEY);
  const abs = url.startsWith("http") ? url : `${API}${url}`;
  const r = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) throw new Error(`Fetch failed: ${r.status} ${url}`);
  return await r.arrayBuffer();
}

export default function PdfFormView() {
  const { id: customerId } = useParams();
  const [params] = useSearchParams();
  const templateId = params.get("template_id");
  const { user, ready } = useAuth();
  const navigate = useNavigate();

  const [fillData, setFillData] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);            // pdfjs document
  const [renderedPages, setRenderedPages] = useState([]); // [{page, canvas dataURL, w, h}]
  const [acroValues, setAcroValues] = useState({});
  const [overlayValues, setOverlayValues] = useState({});
  const [zoom, setZoom] = useState(1.4);
  const [pageNum, setPageNum] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [dirty, setDirty] = useState(false);
  const [missingInfo, setMissingInfo] = useState(null);
  const originalBytesRef = useRef(null); // ArrayBuffer cache

  useEffect(() => {
    if (ready && !isDealerUser(user)) navigate("/dealer-login");
  }, [ready, user, navigate]);

  // Warn on unload if there are unsaved edits.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = "You have unsaved edits.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  /* Step 1 — bundle endpoint */
  const load = useCallback(async () => {
    try {
      setError(""); setBusy(true);
      const r = await api.get(`/admin/crm/customer/${customerId}/template/${templateId}/fill-data`);
      setFillData(r.data);
      setAcroValues({ ...(r.data.acro_values || {}) });
      setOverlayValues({ ...(r.data.overlay_values || {}) });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load form data.");
    } finally { setBusy(false); }
  }, [customerId, templateId]);
  useEffect(() => { if (user && templateId) load(); }, [user, templateId, load]);

  /* Step 2-4 — load + autofill + render */
  const renderWithValues = useCallback(async () => {
    if (!fillData) return;
    setBusy(true); setError("");
    try {
      let bytes = originalBytesRef.current;
      if (!bytes) {
        bytes = await authArrayBuffer(fillData.template.pdf_url);
        originalBytesRef.current = bytes;
      }
      // pdf-lib autofill (AcroForm only)
      let workingBytes = bytes;
      if (fillData.template.has_acroform) {
        const doc = await PDFDocument.load(bytes);
        const form = doc.getForm();
        for (const [name, val] of Object.entries(acroValues || {})) {
          try {
            const field = form.getFieldMaybe ? form.getFieldMaybe(name) : null;
            // pdf-lib >=1.17 has getField + getFieldMaybe; fall back to try/catch
            const f = field || form.getField(name);
            const ctor = f.constructor.name;
            if (ctor === "PDFTextField") {
              f.setText(val == null ? "" : String(val));
            } else if (ctor === "PDFCheckBox") {
              val ? f.check() : f.uncheck();
            } else if (ctor === "PDFRadioGroup") {
              if (val) f.select(String(val));
            } else if (ctor === "PDFDropdown" || ctor === "PDFOptionList") {
              if (val) f.select(String(val));
            }
          } catch { /* missing field — ignore */ }
        }
        workingBytes = await doc.save({ updateFieldAppearances: true });
      }
      // pdfjs render — we ONLY want the rasterized canvas, not the selectable
      // text layer. Otherwise pdfjs draws the AcroForm widget's saved value
      // *inside* the page bitmap AND our overlay input draws it on top, which
      // looks like a doubled / ghosted character.
      const loadingTask = pdfjs.getDocument({ data: workingBytes.slice(0) });
      const doc = await loadingTask.promise;
      setPdfDoc(doc);
      const pages = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale: zoom });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width; canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        // `renderInteractiveForms: false` and a plain canvas render gives us a
        // clean background — the editable inputs live in the React overlay.
        await page.render({ canvasContext: ctx, viewport, renderInteractiveForms: false,
                              annotationMode: 0 /* DISABLE */ }).promise;
        pages.push({
          page: i,
          dataUrl: canvas.toDataURL("image/png"),
          width: viewport.width,
          height: viewport.height,
          pdfWidth: page.getViewport({ scale: 1 }).width,
          pdfHeight: page.getViewport({ scale: 1 }).height,
        });
      }
      setRenderedPages(pages);
      // Audit (only first render — autofill event)
      api.post(`/admin/crm/customer/${customerId}/deal-jacket/pdf-log`, {
        action: "autofilled", template_id: templateId,
        detail: `${Object.keys(acroValues || {}).length} acro + ${Object.keys(overlayValues || {}).length} overlay`,
      }).catch(() => {});
    } catch (e) {
      setError("Render failed: " + (e?.message || String(e)));
    } finally { setBusy(false); }
  }, [fillData, acroValues, overlayValues, zoom, customerId, templateId]);
  useEffect(() => { renderWithValues(); /* eslint-disable-next-line */ }, [fillData, zoom]);

  /* Step 6 — compose flattened PDF on demand (print / save / download) */
  const composeFilledPdf = async ({ flatten = true } = {}) => {
    const bytes = originalBytesRef.current ||
      (originalBytesRef.current = await authArrayBuffer(fillData.template.pdf_url));
    const doc = await PDFDocument.load(bytes);
    const form = doc.getForm();
    // Fill AcroForm
    if (fillData.template.has_acroform) {
      for (const [name, val] of Object.entries(acroValues || {})) {
        try {
          const f = form.getField(name);
          const ctor = f.constructor.name;
          if (ctor === "PDFTextField") f.setText(val == null ? "" : String(val));
          else if (ctor === "PDFCheckBox") val ? f.check() : f.uncheck();
          else if (ctor === "PDFRadioGroup") { if (val) f.select(String(val)); }
          else if (ctor === "PDFDropdown" || ctor === "PDFOptionList") { if (val) f.select(String(val)); }
        } catch { /* missing — ignore */ }
      }
    }
    // Overlay layer — draw text onto each page at the admin-defined coords.
    const overlayFields = (fillData.template.fields || []);
    if (overlayFields.length > 0) {
      const helvetica = await doc.embedFont(StandardFonts.Helvetica);
      const pages = doc.getPages();
      for (const fld of overlayFields) {
        const pi = (fld.page || 1) - 1;
        if (pi < 0 || pi >= pages.length) continue;
        const pg = pages[pi];
        const { width: pw, height: ph } = pg.getSize();
        const val = overlayValues[fld.id];
        if (val == null || val === "") continue;
        const x = (fld.x || 0) * pw;
        // pdf-lib y origin is bottom-left; overlay coords are top-left → invert.
        const y = ph - (fld.y || 0) * ph - (fld.h || 0.03) * ph;
        const fontSize = Math.min(36, Math.max(6, fld.font_size || 12));
        if (fld.type === "checkbox") {
          if (val) pg.drawText("X", { x: x + 2, y: y + 2, size: fontSize, font: helvetica, color: rgb(0, 0, 0) });
        } else {
          // Multi-line: split by \n. Truncate if too wide.
          const text = String(val);
          const lines = text.split(/\r?\n/);
          let dy = 0;
          for (const line of lines) {
            pg.drawText(line, { x: x + 2, y: y + 2 + ((lines.length - 1) - dy) * (fontSize + 2),
                                  size: fontSize, font: helvetica, color: rgb(0, 0, 0) });
            dy++;
          }
        }
      }
    }
    if (flatten && fillData.template.has_acroform) {
      try { form.flatten(); } catch { /* tolerate flatten errors */ }
    }
    return await doc.save();
  };

  const print = async () => {
    setBusy(true); setStatusMsg("");
    try {
      const out = await composeFilledPdf({ flatten: true });
      const blob = new Blob([out], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in a new tab and trigger print as soon as it loads.
      const w = window.open(url, "_blank");
      if (w) {
        w.addEventListener("load", () => { try { w.print(); } catch { /* ignore */ } }, { once: true });
      }
      api.post(`/admin/crm/customer/${customerId}/deal-jacket/pdf-log`,
        { action: "printed", template_id: templateId,
          detail: `${(out.byteLength / 1024).toFixed(1)} KB` }).catch(() => {});
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setStatusMsg("Print failed: " + (e?.message || e));
    } finally { setBusy(false); }
  };

  const download = async () => {
    if (user?.role && ["readonly", "guest"].includes(user.role)) {
      setStatusMsg("Your role can't download forms.");
      return;
    }
    setBusy(true); setStatusMsg("");
    try {
      const out = await composeFilledPdf({ flatten: true });
      const blob = new Blob([out], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      a.href = url; a.download = `${fillData.template.name}-${stamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      api.post(`/admin/crm/customer/${customerId}/deal-jacket/pdf-log`,
        { action: "downloaded", template_id: templateId,
          detail: `${(out.byteLength / 1024).toFixed(1)} KB` }).catch(() => {});
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      setStatusMsg("Download failed: " + (e?.message || e));
    } finally { setBusy(false); }
  };

  const saveToJacket = async ({ markFinal = false } = {}) => {
    setBusy(true); setStatusMsg(""); setMissingInfo(null);
    try {
      const out = await composeFilledPdf({ flatten: true });
      // base64 encode
      let bin = ""; const arr = new Uint8Array(out);
      for (let i = 0; i < arr.byteLength; i++) bin += String.fromCharCode(arr[i]);
      const b64 = btoa(bin);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      const r = await api.post(`/admin/crm/customer/${customerId}/deal-jacket/save-filled-pdf`, {
        template_id: templateId,
        template_version: fillData.template.version,
        acro_values: acroValues,
        overlay_values: overlayValues,
        file_b64: b64,
        filename: `${fillData.template.name}-${stamp}.pdf`,
        flattened: true,
        mark_final: markFinal,
      });
      setStatusMsg(`${markFinal ? "Final saved" : "Draft saved"} · ${r.data?.filename || "OK"}`);
      setDirty(false);
    } catch (e) {
      const det = e?.response?.data?.detail;
      // The missing-info blocker returns a structured 422 with a missing list.
      if (det && typeof det === "object" && det.code === "missing_info") {
        setMissingInfo(det);
        setStatusMsg(`✕ Cannot save Final — ${det.missing?.length || 0} required fields missing for ${det.deal_type} deals.`);
      } else {
        setStatusMsg("Save failed: " + (det || e.message));
      }
    } finally { setBusy(false); }
  };

  const setAcro = (name, val) => { setAcroValues(v => ({ ...v, [name]: val })); setDirty(true); };
  const setOverlay = (id, val) => { setOverlayValues(v => ({ ...v, [id]: val })); setDirty(true); };

  const visiblePage = useMemo(() => renderedPages.find(p => p.page === pageNum) || renderedPages[0],
    [renderedPages, pageNum]);

  if (!fillData) {
    return (
      <div className="ssc-container ssc-section" data-testid="pdf-form-loading">
        {busy ? "Loading form…" : error || "Loading…"}
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen pb-24" data-testid="pdf-form-view">
      {/* Toolbar */}
      <div className="bg-white border-b sticky top-0 z-20 print:hidden">
        <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center gap-3">
          <Link to={`/admin/crm/customer/${customerId}`} className="text-xs text-blue-600 hover:underline">← Back to Deal Jacket</Link>
          <div className="flex-1">
            <div className="font-bold text-sm text-[#061426]" data-testid="pdf-form-title">
              {fillData.template.name} <span className="text-xs text-gray-500">v{fillData.template.version}</span>
              {fillData.template.has_acroform ? (
                <span className="ml-2 text-[10px] uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">AcroForm</span>
              ) : (
                <span className="ml-2 text-[10px] uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">Overlay</span>
              )}
              {dirty && <span className="ml-2 text-[10px] text-amber-700 font-bold">● Unsaved edits</span>}
            </div>
            <div className="text-[10px] text-gray-500">For: {fillData.customer.name || "—"}</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setZoom(z => Math.max(0.5, z - 0.2))} className="px-2 py-1 text-xs border rounded" data-testid="pdf-zoom-out">−</button>
            <span className="text-xs w-12 text-center" data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(3, z + 0.2))} className="px-2 py-1 text-xs border rounded" data-testid="pdf-zoom-in">+</button>
          </div>
          {renderedPages.length > 1 && (
            <div className="flex items-center gap-1 text-xs">
              <button onClick={() => setPageNum(p => Math.max(1, p - 1))} className="px-2 py-1 border rounded" data-testid="pdf-page-prev">‹</button>
              <span data-testid="pdf-page-indicator">{pageNum} / {renderedPages.length}</span>
              <button onClick={() => setPageNum(p => Math.min(renderedPages.length, p + 1))} className="px-2 py-1 border rounded" data-testid="pdf-page-next">›</button>
            </div>
          )}
          <button onClick={() => saveToJacket({ markFinal: false })} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white font-bold disabled:opacity-50" data-testid="pdf-save-to-jacket">Save Draft</button>
          <button onClick={() => saveToJacket({ markFinal: true })} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white font-bold disabled:opacity-50" data-testid="pdf-save-final" title="Lock as the Final version. Blocked if required customer info is missing.">Save Final 🔒</button>
          <button onClick={print} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white font-bold disabled:opacity-50" data-testid="pdf-print">Print</button>
          <button onClick={download} disabled={busy || ["readonly", "guest"].includes(user?.role)} className="text-xs px-3 py-1.5 rounded border bg-white disabled:opacity-50" data-testid="pdf-download">Download</button>
        </div>
        {statusMsg && <div className="max-w-6xl mx-auto px-4 pb-2 text-xs text-gray-700" data-testid="pdf-status">{statusMsg}</div>}
        {error && <div className="max-w-6xl mx-auto px-4 pb-2 text-xs text-red-600" data-testid="pdf-error">{error}</div>}
        {missingInfo && (
          <div className="max-w-6xl mx-auto px-4 pb-3" data-testid="pdf-missing-info-panel">
            <div className="bg-red-50 border border-red-300 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-red-700">Cannot finalize</div>
                  <div className="text-sm font-bold text-red-900">{missingInfo.missing?.length || 0} required fields missing for {missingInfo.deal_type} deals</div>
                </div>
                <button onClick={() => setMissingInfo(null)} className="text-red-700 hover:text-red-900 text-lg leading-none">×</button>
              </div>
              <ul className="space-y-1">
                {(missingInfo.missing || []).map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-red-600">✕</span>
                    <div className="flex-1">
                      <div className="font-semibold text-red-900">{m.label}</div>
                      {m.hint && <div className="text-red-700/70 text-[11px]">{m.hint}</div>}
                    </div>
                    <a href={`/admin/crm/customer/${customerId}#tab=${m.tab}`}
                       className="text-[10px] bg-red-600 hover:bg-red-500 text-white font-bold px-2 py-1 rounded whitespace-nowrap"
                       data-testid={`pdf-missing-jump-${m.tab}`}>
                       Jump to {m.tab.replace("_", " ")} →
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-[11px] text-red-700/70">Save Draft is still available — Final is blocked until all required customer info is on file.</div>
            </div>
          </div>
        )}
      </div>

      {/* PDF + overlay */}
      <div className="max-w-6xl mx-auto py-6 px-3 flex gap-4">
        {/* Page thumbnails (sidebar) */}
        {renderedPages.length > 1 && (
          <aside className="w-24 flex-shrink-0 space-y-2 sticky top-20 self-start" data-testid="pdf-thumbs">
            {renderedPages.map(p => (
              <button key={p.page} type="button" onClick={() => setPageNum(p.page)}
                className={`block w-full border-2 rounded overflow-hidden ${pageNum === p.page ? "border-blue-500" : "border-gray-200"}`}
                data-testid={`pdf-thumb-${p.page}`}>
                <img src={p.dataUrl} alt={`Page ${p.page}`} className="w-full" />
                <div className="text-[10px] text-center bg-white py-0.5">Page {p.page}</div>
              </button>
            ))}
          </aside>
        )}

        <main className="flex-1 flex flex-col items-center">
          {visiblePage && (
            <PdfPageOverlay
              page={visiblePage}
              acroFields={(fillData.template.acro_fields || []).filter(f => f.page === visiblePage.page)}
              overlayFields={(fillData.template.fields || []).filter(f => (f.page || 1) === visiblePage.page)}
              acroValues={acroValues}
              overlayValues={overlayValues}
              onAcroChange={setAcro}
              onOverlayChange={setOverlay}
              readonly={["readonly", "guest"].includes(user?.role)}
            />
          )}
          {!visiblePage && busy && <div className="text-gray-500 py-12">Rendering…</div>}
        </main>
      </div>
    </div>
  );
}

/* Single-page render with editable overlay inputs. */
function PdfPageOverlay({ page, acroFields, overlayFields, acroValues, overlayValues, onAcroChange, onOverlayChange, readonly }) {
  return (
    <div className="bg-white shadow-lg relative inline-block" style={{ width: page.width, height: page.height }} data-testid={`pdf-page-${page.page}`}>
      <img src={page.dataUrl} alt={`Page ${page.page}`} style={{ width: page.width, height: page.height, display: "block" }} draggable={false} />
      {/* AcroForm widget overlays — coords are in PDF points (bottom-left origin), convert to CSS px. */}
      {acroFields.map(f => {
        const [x1, y1, x2, y2] = f.rect || [0, 0, 0, 0];
        const sx = page.width / page.pdfWidth;
        const sy = page.height / page.pdfHeight;
        const left = x1 * sx;
        const w = (x2 - x1) * sx;
        const h = (y2 - y1) * sy;
        const top = page.height - y2 * sy; // pdf top = bottom of viewport
        const value = acroValues[f.name];
        const test = `acro-input-${f.name}`;
        if (f.type === "checkbox") {
          return (
            <input key={f.name} type="checkbox" checked={!!value}
              disabled={readonly}
              onChange={(e) => onAcroChange(f.name, e.target.checked)}
              style={{ position: "absolute", left, top, width: w, height: h, cursor: readonly ? "not-allowed" : "pointer" }}
              data-testid={test} />
          );
        }
        if (f.type === "multiline") {
          return (
            <textarea key={f.name} value={value || ""} readOnly={readonly}
              onChange={(e) => onAcroChange(f.name, e.target.value)}
              className="absolute bg-white border border-blue-300 focus:border-blue-500 outline-none"
              style={{ left, top, width: w, height: h, fontSize: Math.max(10, h * 0.5), padding: 2, resize: "none" }}
              data-testid={test} />
          );
        }
        if (f.type === "dropdown" && f.options) {
          return (
            <select key={f.name} value={value || ""} disabled={readonly}
              onChange={(e) => onAcroChange(f.name, e.target.value)}
              className="absolute bg-white border border-blue-300"
              style={{ left, top, width: w, height: h, fontSize: Math.max(10, h * 0.55) }}
              data-testid={test}>
              <option value=""></option>
              {f.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        }
        return (
          <input key={f.name} value={value || ""} readOnly={readonly}
            onChange={(e) => onAcroChange(f.name, e.target.value)}
            className="absolute bg-white border border-blue-300 focus:border-blue-500 outline-none"
            style={{ left, top, width: w, height: h, fontSize: Math.max(10, h * 0.55), padding: 2 }}
            maxLength={f.max_length || undefined}
            data-testid={test} />
        );
      })}

      {/* Flat-scan overlay fields — coords are 0..1 percent of page. */}
      {overlayFields.map(f => {
        const left = (f.x || 0) * page.width;
        const top = (f.y || 0) * page.height;
        const w = (f.w || 0.18) * page.width;
        const h = (f.h || 0.035) * page.height;
        const test = `overlay-input-${f.id}`;
        const val = overlayValues[f.id];
        const baseStyle = { position: "absolute", left, top, width: w, height: h,
                            fontSize: (f.font_size || 12) + "px", textAlign: f.alignment || "left" };
        if (f.type === "checkbox") {
          return (
            <input key={f.id} type="checkbox" checked={!!val} disabled={readonly}
              onChange={(e) => onOverlayChange(f.id, e.target.checked)}
              style={baseStyle} data-testid={test} />
          );
        }
        if (f.type === "notes" || f.type === "multiline") {
          return (
            <textarea key={f.id} value={val || ""} readOnly={readonly}
              onChange={(e) => onOverlayChange(f.id, e.target.value)}
              className="bg-yellow-50/60 border border-dashed border-blue-300 focus:border-blue-500 focus:bg-white"
              style={{ ...baseStyle, resize: "none", padding: 2 }} data-testid={test} />
          );
        }
        return (
          <input key={f.id} value={val || ""} readOnly={readonly}
            onChange={(e) => onOverlayChange(f.id, e.target.value)}
            className="bg-yellow-50/60 border border-dashed border-blue-300 focus:border-blue-500 focus:bg-white"
            style={{ ...baseStyle, padding: 2 }} data-testid={test} />
        );
      })}
    </div>
  );
}
